/* eslint-disable jsx-a11y/no-redundant-roles */
import React from 'react';
import PropTypes from 'prop-types';

// Library
import shortid from 'shortid';

// Cybs
import CybsButton from '@cybs-ui/components/lib/button';
import { StructFlex } from '@cybs-ui/components/lib/struct-flex';
import CybsButtonGroup from '@cybs-ui/components/lib/button-group';

// VDS
import Icon from '@visa/vds-react/icon-styled';

import classes from '../component.styles';

// Utility functions
import { fileSize, objectToArray } from '../utils';

class CybsSingleFileUpload extends React.Component {
  constructor(props) {
    super(props);
    const { selectedFile } = this.props;
    this.state = {
      files: selectedFile || [],
      fileRemoved: false
    };

    this.fileInputRef = React.createRef();
    this.browseButtonRef = React.createRef();
    this.id = `file-upload-${shortid.generate()}`;
  }

  openFileSelect = () => {
    this.fileInputRef.current.click();
  };

  focusBrowseButton = () => {
    if (this.browseButtonRef && this.browseButtonRef.current) {
      this.browseButtonRef.current.focus();
    }
  };

  handleFileSelection = fileSelection => {
    if (fileSelection) {
      const fileArray = objectToArray(fileSelection);

      const { onRetrieveFiles } = this.props;
      this.setState({ files: fileArray, fileRemoved: false }, () => {
        const { files } = this.state;
        onRetrieveFiles(files);
        this.focusBrowseButton();
      });
    }
  };

  handleFileRemoval = () => {
    const { onRetrieveFiles } = this.props;
    this.setState(
      {
        files: [],
        fileRemoved: true
      },
      () => {
        this.fileInputRef.current.value = null;
        onRetrieveFiles([]);
        this.focusBrowseButton();
      }
    );
  };

  render() {
    const {
      accept,
      customCSS,
      dataTest,
      headingLevel,
      i18n: i18nProp,
      required,
      onRetrieveFiles,
      selectedFile,
      ...buttonProps
    } = this.props;

    const { files, fileRemoved } = this.state;

    const i18n = {
      ...CybsSingleFileUpload.defaultProps.i18n,
      ...i18nProp
    };
    return (
      <StructFlex
        direction="column"
        css={theme => [classes(theme).container, customCSS(theme)]}
      >
        <h3
          role="heading"
          aria-level={headingLevel}
          css={theme => classes(theme).title}
          id={`file-upload-title-${this.id}`}
        >
          {`${i18n.title} ${required ? '*' : ''}`}
        </h3>
        <p
          id={`file-upload-description-${this.id}`}
          css={theme => classes(theme).subtitle}
        >
          {i18n.description}
        </p>
        <fieldset css={theme => classes(theme).fieldSetClass} required={required}>
        <legend
          aria-labelledby={`file-upload-title-${this.id} file-upload-description-${this.id}`}
          className="cybs-a11y-hidden"
        >
              <span aria-hidden="true">
                {i18n.errorText === '' && files[0] && files[0].name
                  ? `${files[0].name} (${fileSize(files[0].size)})`
                  : `${i18n.noFilesSelected}`}
              </span>
        </legend>
        <StructFlex
          data-test={`${dataTest}-cybsFileUpload`}
          direction="row"
          css={theme => classes(theme, i18n.errorText !== '').body}
        >
          <div>
            <div css={theme => classes(theme).inputUploadWrapper}>
              <div aria-live="assertive" className="cybs-a11y-hidden">
                {i18n.errorText === '' && files[0] && files[0].name
                  ? i18n.fileSelectedLabel
                  : ''}
                {fileRemoved ? i18n.fileRemovedLabel : ''}
              </div>
              <span id={`file-upload-filename-${this.id}`} css={theme => classes(theme).fileSection}>
                {i18n.errorText === '' && files[0] && files[0].name
                  ? `${files[0].name} (${fileSize(files[0].size)})`
                  : `${i18n.noFilesSelected}`}
              </span>
              <input
                ref={this.fileInputRef}
                type="file"
                id={this.id}
                data-test={`${dataTest}-CybsUpload-input`}
                aria-hidden="true"
                aria-labelledby={`file-upload-title-${this.id}`}
                tabIndex="-1"
                onChange={e =>
                  this.handleFileSelection(this.fileInputRef.current.files, e)
                }
                accept={accept || '*'}
                css={theme => classes(theme).inputUploadWrapper}
              />
            </div>
            <CybsButtonGroup dataTest={dataTest}>
              <CybsButton
                dataTest={`${dataTest}-CybsUpload-browseBtn`}
                {...buttonProps}
                i18n={{ label: i18n.browseBtnLabel }}
                colorScheme="secondary"
                onClick={this.openFileSelect}
                ref={this.browseButtonRef}
                aria-describedby={
                  i18n.errorText !== '' ? `error-label-${this.id}` : `file-upload-filename-${this.id}`
                }
              />
              <CybsButton
                dataTest={`${dataTest}-CybsUpload-removeBtn`}
                {...buttonProps}
                disabled={!(i18n.errorText === '' && files[0] && files[0].name)}
                i18n={{ label: i18n.removeBtnLabel }}
                colorScheme="secondary"
                onClick={this.handleFileRemoval}
              />
            </CybsButtonGroup>
          </div>
        </StructFlex>
        </fieldset>

        <div
          data-test={`${dataTest}-CybsUpload-errorText`}
          css={theme => classes(theme, i18n.errorText !== '').errorText}
          aria-invalid="true"
        >
          <Icon name="error" resolution="tiny" />
          <span id={`error-label-${this.id}`}>{i18n.errorText}</span>
        </div>
      </StructFlex>
    );
  }
}

CybsSingleFileUpload.displayName = 'SingleCybsUpload';

CybsSingleFileUpload.propTypes = {
  /**
   * String with a pattern to be fulfilled by the files
   */
  accept: PropTypes.string,
  /**
   * Specified the color scheme used in the buttons
   */
  colorScheme: PropTypes.oneOf(['default', 'primary', 'secondary', 'tertiary']),
  /**
   * Styling function to allow CSS overrides via Emotion
   *
   * @ignore
   */
  customCSS: PropTypes.func,
  /**
   * Data Test
   */
  dataTest: PropTypes.string.isRequired,
  /**
   * Level of the heading displayed before the component
   */
  headingLevel: PropTypes.number,
  /**
   * Localization
   */
  i18n: PropTypes.shape({
    description: PropTypes.string,
    fileSelectedLabel: PropTypes.string,
    fileRemovedLabel: PropTypes.string,
    title: PropTypes.string,
    errorText: PropTypes.string,
    noFilesSelected: PropTypes.string,
    browseBtnLabel: PropTypes.string,
    removeBtnLabel: PropTypes.string
  }),
  /**
   * Indicates if the field is required
   */
  required: PropTypes.bool,
  /**
   * Function to be executed when the files are retrieved by the browser
   */
  onRetrieveFiles: PropTypes.func.isRequired,
  /**
   * Selected FIles
   */
  selectedFile: PropTypes.arrayOf(PropTypes.object)
};

CybsSingleFileUpload.defaultProps = {
  accept: '*',
  colorScheme: 'primary',
  customCSS: () => '',
  headingLevel: 3,
  required: false,
  i18n: {
    browseBtnLabel: 'Browse',
    description: '',
    errorText: '',
    noFilesSelected: 'Select a file to upload.',
    fileSelectedLabel: 'File selected',
    fileRemovedLabel: 'File removed',
    removeBtnLabel: 'Remove',
    title: 'Upload File'
  },
  selectedFile: []
};

export default CybsSingleFileUpload;
